import React, { useState, useEffect, useCallback, memo } from 'react';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import NoData from '../../../../components/common/NoData';

import { getCourseCompletePercentage } from '../../../../services/user/course';

// Memoize the progress bar component
const ProgressBar = memo(({ progress }) => {
  const capped = Math.min(100, Math.max(0, Number.isFinite(progress) ? progress : 0));
  return (
  <div className="tw-h-[8px] tw-bg-[#e0e0e0] tw-rounded-[4px] tw-overflow-hidden progress-bar-container" role="progressbar"
    aria-valuenow={capped} aria-valuemin="0" aria-valuemax="100"
    style={{
      height: '8px',
      backgroundColor: '#e9ecef',
      borderRadius: '4px',
      overflow: 'hidden',
      margin: '6px 0 0'
    }}>
    <div
      className="tw-h-full tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] tw-rounded-full [transition:width_0.3s_ease] tw-flex tw-items-center tw-justify-center [--tw-bg-opacity:1] tw-text-[0.75rem] tw-leading-[1rem] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [transition-property:width] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:300ms] ui-progress-bar"
      style={{
        width: `${capped}%`,
        height: '100%',
        backgroundColor: '#0d6efd',
        transition: 'width 0.6s ease'
      }}
    ></div>
  </div>
  );
});

function CourseModule({
  courseModules,
  courseId,
  onContentClick,
  selectedContent,
  onProgressUpdate,
}) {
  const [expandedModule, setExpandedModule] = useState(() => {
    const saved = localStorage.getItem(`module-expanded-${courseId}`);
    return saved ? JSON.parse(saved) : null;
  });
  const [courseProgress, setCourseProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize the progress update function
  const updateProgress = useCallback(async () => {
    if (!courseId) return;

    try {
      const response = await getCourseCompletePercentage(courseId);
      if (response?.data?.overallCompletionPercentage != null) {
        const raw = parseFloat(response.data.overallCompletionPercentage);
        const progress = Math.min(100, Math.max(0, Number.isFinite(raw) ? raw : 0));
        setCourseProgress(prev => {
          // Only update if progress has changed
          if (prev !== progress) {
            if (onProgressUpdate) {
              onProgressUpdate(progress);
            }
            return progress;
          }
          return prev;
        });
      }
    } catch (error) {
      // Error updating course progress
    }
  }, [courseId, onProgressUpdate]);

  useEffect(() => {
    if (selectedContent && courseModules) {
      const moduleWithContent = courseModules.find(module =>
        module.content?.some(item => String(item.id) === String(selectedContent.id))
      );
      if (moduleWithContent) {
        setExpandedModule(moduleWithContent.id);
      }
    }
  }, [selectedContent, courseModules]);

  // Initial progress fetch
  useEffect(() => {
    if (courseId) {
      setIsLoading(true);
      updateProgress().finally(() => setIsLoading(false));
    }
  }, [courseId, updateProgress]);

  // Handle progress updates
  useEffect(() => {
    if (courseModules) {
      updateProgress();
    }
  }, [courseModules, updateProgress]);

  useEffect(() => {
    if (expandedModule !== null) {
      localStorage.setItem(`module-expanded-${courseId}`, JSON.stringify(expandedModule));
    }
  }, [expandedModule, courseId]);

  const getContentIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'video':
        return 'mdi:play-circle-outline';
      case 'assessment':
        return 'mdi:help-circle-outline';
      case 'document':
        return 'mdi:file-document-outline';
      case 'survey':
        return 'mdi:clipboard-text-outline';
      default:
        return 'mdi:circle-outline';
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModule(String(expandedModule) === String(moduleId) ? null : moduleId);
  };

  const calculateModuleProgress = (module) => {
    if (!Array.isArray(module.content) || module.content.length === 0) return 0;
    const completedItems = module.content.filter(item => Number(item.completed) === 1).length;
    return (completedItems / module.content.length) * 100;
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  if (isLoading) {
    return (
      <div className={"tw-mt-8 tw-p-[24px] tw-bg-[white] tw-rounded-[12px] tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.08)] tw-overflow-y-auto [&_h2]:tw-text-[24px] [&_h2]:tw-mb-[10px] [&_h2]:tw-text-[#333] [&_.content-item:hover]:!tw-bg-[#eef1ff] [&_.content-item.active]:!tw-bg-[#eef1ff] [&_.content-item.active]:tw-border-l-[4px] [&_.content-item.active]:tw-border-solid [&_.content-item.active]:tw-border-l-[#3152e8] [&_.content-item.active:hover]:!tw-bg-[#eef1ff] [&_.content-item.active:hover]:tw-border-l-[4px] [&_.content-item.active:hover]:tw-border-solid [&_.content-item.active:hover]:tw-border-l-[#3152e8] [&_.content-item.locked:hover]:!tw-bg-[#f1f5f9] max-[768px]:tw-p-[15px] course-content"} aria-busy="true">
        <h2>Course Content</h2>
        <div className="tw-text-center tw-p-6">
          <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600" role="status">
            <span className="tw-sr-only">Loading course progress...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={"tw-mt-8 tw-p-[24px] tw-bg-[white] tw-rounded-[12px] tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.08)] tw-overflow-y-auto [&_h2]:tw-text-[24px] [&_h2]:tw-mb-0 [&_h2]:tw-text-[#333] [&_.content-item:hover]:!tw-bg-[#eef1ff] [&_.content-item.active]:!tw-bg-[#eef1ff] [&_.content-item.active]:tw-border-l-[4px] [&_.content-item.active]:tw-border-solid [&_.content-item.active]:tw-border-l-[#3152e8] [&_.content-item.active:hover]:!tw-bg-[#eef1ff] [&_.content-item.active:hover]:tw-border-l-[4px] [&_.content-item.active:hover]:tw-border-solid [&_.content-item.active:hover]:tw-border-l-[#3152e8] [&_.content-item.locked:hover]:!tw-bg-[#f1f5f9] max-[768px]:tw-p-[15px] course-content"} role="navigation" aria-label="Course modules">
      <h2>Course Content</h2>
      <div className="tw-mt-2 tw-mb-3 progress-info">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-1">
          <span className="tw-text-sm tw-font-medium tw-text-slate-600">{Math.floor(courseProgress)}% completed</span>
          <Link to="/user/certificates" className="tw-text-sm tw-font-medium tw-text-indigo-600 hover:tw-text-indigo-700 hover:tw-underline">Generate Certificate</Link>
        </div>
        <ProgressBar progress={courseProgress} />
      </div>

      <div className={"tw-flex tw-flex-col tw-gap-[15px] tw-mt-0 tw-max-h-[calc(100vh_-_10px)] tw-overflow-y-auto tw-pr-4 [&::-webkit-scrollbar]:tw-w-[6px] [&::-webkit-scrollbar-track]:tw-bg-[#f1f1f1] [&::-webkit-scrollbar-track]:tw-rounded-[3px] [&::-webkit-scrollbar-thumb]:tw-bg-[#888] [&::-webkit-scrollbar-thumb]:tw-rounded-[3px] [&::-webkit-scrollbar-thumb:hover]:tw-bg-[#555] modules-list"}>
        {Array.isArray(courseModules) && courseModules.map((module, index) => {
          const moduleProgress = calculateModuleProgress(module);
          return (
            <div key={module.id} className="tw-border-[1px] tw-border-solid tw-border-[#e9ecef] tw-rounded-[10px] tw-mb-[5px] tw-overflow-hidden tw-shadow-[0_2px_8px_rgba(0,_0,_0,_0.05)] module-item">
              <div
                className={`[&_svg]:tw-text-[#6c757d] [&_svg]:tw-text-[0.875rem] [&:hover]:tw-bg-[#f0f0f0] [&.expanded]:tw-bg-[#3152e8] [&.expanded]:tw-text-[white] [&.expanded]:tw-border-l-[4px] [&.expanded]:tw-border-solid [&.expanded]:tw-border-l-[#1e3ad2] tw-bg-[#f8f9fa] [transition:all_0.2s_ease] tw-p-4 tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-border-l-[4px] tw-border-solid tw-border-l-[transparent] tw-rounded-[8px_8px_0_0] [&.active]:tw-bg-[#e9ecef] [&.active]:tw-border-b-[1px] [&.active]:tw-border-solid [&.active]:tw-border-b-[#e0e0e0] module-header   ${String(expandedModule) === String(module.id) ? "expanded" : ""}`}
                onClick={() => toggleModule(module.id)}
                onKeyPress={(e) => handleKeyPress(e, () => toggleModule(module.id))}
                role="button"
                tabIndex={0}
                aria-expanded={String(expandedModule) === String(module.id)}
                aria-controls={`module-content-${module.id}`}
              >
                <div className={"[&_h3]:tw-m-0 [&_h3]:tw-text-[16px] [&_h3]:tw-font-medium tw-min-w-[0] [flex:1] tw-mr-[12px] [&_p]:tw-m-[5px_0_0] [&_p]:tw-text-[14px] [&_p]:tw-opacity-[0.8] [&_p]:tw-whitespace-nowrap [&_p]:tw-overflow-hidden [&_p]:tw-text-ellipsis module-title"}>
                  <p className="font-size-14">Module {index + 1}</p>
                  <p className="font-size-14">{module.module_name}</p>
                  <div className="module-progress"
                    role="progressbar"
                    aria-valuenow={moduleProgress}
                    aria-valuemin="0"
                    aria-valuemax="100">
                    <div className="progress-bar-mini">
                      {/* <div className="progress-fill" style={{ width: `${moduleProgress}%` }}></div> */}
                    </div>
                    {/* <span className="progress-text">{Math.round(moduleProgress)}%</span> */}
                  </div>
                </div>
                <Icon
                  icon={String(expandedModule) === String(module.id) ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                  width="24"
                  height="24"
                  className={String(expandedModule) === String(module.id) ? '!tw-text-white' : 'tw-text-[#6c757d]'}
                  aria-hidden="true"
                />
              </div>

              {String(expandedModule) === String(module.id) && (
                <div
                  id={`module-content-${module.id}`}
                  className={"tw-p-0 tw-bg-[white] tw-max-h-[60vh] tw-min-h-[0] tw-overflow-y-auto [-webkit-overflow-scrolling:touch] [scrollbar-width:thin] [scrollbar-color:#3152e8_#f0f0f0] [&::-webkit-scrollbar]:tw-w-[8px] [&::-webkit-scrollbar-track]:tw-bg-[#f0f0f0] [&::-webkit-scrollbar-track]:tw-rounded-[4px] [&::-webkit-scrollbar-thumb]:tw-bg-[#3152e8] [&::-webkit-scrollbar-thumb]:tw-rounded-[4px] [&::-webkit-scrollbar-thumb:hover]:tw-bg-[#1e3ad2] [&_.content-item:hover]:!tw-bg-[#eef1ff] [&_.content-item.locked:hover]:!tw-bg-[#f1f5f9] max-[768px]:tw-max-h-[none] max-[768px]:tw-overflow-visible module-content"}
                  role="region"
                  aria-label={`Content for ${module.module_name}`}
                >
                  {Array.isArray(module.content) && module.content.length > 0 ? (
                    module.content.map((item) => (
                      <div
                        key={`${item.id}-${item.serial_no}`}
                        className={`tw-flex tw-items-center tw-justify-between tw-gap-[12px] tw-p-[16px_20px] tw-border-b-[1px] tw-border-solid tw-border-b-[#f0f0f0] [transition:all_0.2s_ease] tw-relative tw-overflow-hidden [&.active::before]:[content:''] [&.active::before]:tw-absolute [&.active::before]:tw-left-0 [&.active::before]:tw-top-0 [&.active::before]:tw-h-full [&.active::before]:tw-w-[4px] [&.active::before]:tw-bg-[#3152e8] [&.active_.content-icon]:tw-bg-[rgba(49,_82,_232,_0.2)] [&.active_.content-title]:tw-text-[#3152e8] [&.active_.content-title]:tw-font-semibold [&:last-child]:tw-border-b-0 content-item ${String(selectedContent?.id) === String(item.id) ? "active" : ""}`}
                        onClick={() => onContentClick(item)}
                        onKeyPress={(e) => handleKeyPress(e, () => onContentClick(item))}
                        role="button"
                        tabIndex={0}
                        aria-label={`${item.title} - ${item.type} ${item.duration ? `(${item.duration})` : ''}`}
                        aria-current={String(selectedContent?.id) === String(item.id) ? 'true' : 'false'}
                      >
                        <div className="tw-flex tw-items-center tw-gap-[14px] [flex:1] tw-min-w-[0] content-left">
                          <div className="tw-text-[#3152e8] tw-flex tw-items-center tw-justify-center tw-w-[36px] tw-h-[36px] tw-bg-[rgba(49,_82,_232,_0.1)] tw-rounded-[8px] [transition:background-color_0.2s_ease] content-icon">
                            <Icon
                              icon={getContentIcon(item.type)}
                              width="20"
                              height="20"
                              aria-hidden="true"
                            />
                          </div>
                          <div className="tw-flex tw-flex-col tw-gap-[4px] tw-min-w-[0] [flex:1] max-[768px]:tw-flex-col max-[768px]:tw-items-start content-details">
                            <span className="tw-text-sm tw-font-semibold tw-leading-5 tw-text-[#333] tw-block tw-min-w-0 [overflow-wrap:anywhere] content-title">{item.title}</span>
                            <span className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-[#666] tw-capitalize content-type">
                              {item.type === 'assessment' && item.is_adaptive
                                ? 'Adaptive assessment'
                                : item.type}
                            </span>
                            {item.type === 'assessment' && Number(item.is_adaptive) === 1 && (
                              <span className="tw-inline-flex tw-items-center tw-w-[fit-content] tw-p-[3px_8px] tw-rounded-[999px] tw-bg-[#eff6ff] tw-text-[#1d4ed8] tw-text-[11px] tw-font-bold tw-tracking-[0.02em] content-badge">Adaptive</span>
                            )}
                          </div>
                        </div>
                        <div className={"[flex:0_0_auto] tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-p-[8px] tw-rounded-[50%] [transition:all_0.2s_ease] [&:hover]:tw-bg-[#f0f0f0] [&_.completed]:tw-text-[#4CAF50] [&_.completed]:[filter:drop-shadow(0_0_2px_rgba(76,_175,_80,_0.3))] [&_.pending]:tw-text-[#ddd] [&_.locked]:tw-text-[#94a3b8] [&:hover_.pending]:tw-text-[#bbb] content-status"}>
                          <Icon
                            icon={Number(item.completed) === 1 ? "mdi:check-circle" : "mdi:circle-outline"}
                            className={Number(item.completed) === 1 ? "completed" : "pending"}
                            width="24"
                            height="24"
                            aria-label={Number(item.completed) === 1 ? "Completed" : "Not completed"}
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <NoData
                      message="No course items available in this module yet."
                      icon="mdi:information-outline"
                      compact
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CourseModule;
